/**
 * Visual style options for project assets.
 * Single source of truth used by landing page and passed to agentic backend.
 *
 * The style value is passed directly to Gemini's image generation prompt
 * (e.g., "Art style: cinematic"), so values should be descriptive art style
 * terms that the model understands.
 *
 * The `prompt` field contains detailed style descriptions used by the Style Library
 * system (see docs/rfc-style-library.md). These prompts are included in generation
 * requests to produce consistent visual output.
 */

export const VISUAL_STYLES = [
  {
    value: 'real-person-cinematic' as const,
    label: 'Cinematic',
    icon: 'film',
    color: '#ef4444',
    prompt: 'Photorealistic cinematic style with natural lighting, film-grade color grading, realistic skin textures, shallow depth of field, and dramatic compositions inspired by Korean drama cinematography',
    tags: ['Western'],
  },
  {
    value: '2d-comic' as const,
    label: '2D Comic',
    icon: 'pencil',
    color: '#6366f1',
    prompt: 'Bold 2D comic book style with strong ink outlines, flat colors, cel-shading, dynamic panel compositions, and expressive character poses typical of manhwa and webtoon art',
    tags: ['Western', 'Illustrative'],
  },
  {
    value: '3d-comic' as const,
    label: '3D Animated',
    icon: 'cube',
    color: '#8b5cf6',
    prompt: '3D rendered comic style with smooth shading, volumetric lighting, stylized proportions, and a polished CGI look reminiscent of modern animated films and 3D webtoons',
    tags: ['3D'],
  },
  {
    value: 'romance-fantasy' as const,
    label: 'Fantasy',
    icon: 'sparkles',
    color: '#ec4899',
    prompt: 'manhwa style, korean webtoon aesthetic, romance fantasy, highly detailed, jewel-like eyes, intricate clothing details, ornate lace and gold trim, soft diffused lighting, bloom effect, dreamy atmosphere, sparkling particles, pastel and rich colors, elegant, aristocratic setting',
    tags: ['Korean', 'Eastern', 'Illustrative'],
  },
  {
    value: 'dark-fantasy' as const,
    label: 'Dark Fantasy',
    color: '#1e293b',
    prompt: 'Dark fantasy illustration style, richly detailed digital painting with deep shadows and moody chiaroscuro lighting, desaturated color palette dominated by deep purples, charcoal blacks, and muted golds, intricate gothic ornamentation and weathered medieval textures, heavy atmospheric fog and volumetric light shafts, bold linework with gritty crosshatching details, ominous and foreboding atmosphere inspired by Western gothic and sword-and-sorcery traditions.',
    tags: ['Western', 'Illustrative'],
  },
  {
    value: 'ink-wash-oriental' as const,
    label: 'Ink Wash Oriental',
    color: '#6366f1',
    prompt: 'Ink wash painting style inspired by East Asian sumi-e and shanshui traditions, fluid black ink brushstrokes with varying opacity on textured rice paper, monochromatic palette with subtle warm sepia undertones, minimalist compositions with generous negative space, delicate calligraphic linework blending into soft washes, atmospheric perspective achieved through ink dilution, contemplative and serene mood with Zen-inspired simplicity.',
    tags: ['Eastern', 'Illustrative'],
  },
  {
    value: 'retro-anime' as const,
    label: 'Retro Anime',
    color: '#fbbf24',
    prompt: '1980s–90s retro anime cel animation style, hand-painted cel-shaded coloring with visible paint texture, warm analog color palette with slightly desaturated hues, dramatic spotlight lighting with bold shadow edges, thick confident outlines with expressive line weight variation, soft airbrushed highlights on hair and metallic surfaces, nostalgic VHS-era grain, inspired by classic Japanese animation studios.',
    tags: ['Anime', 'Pop Culture', 'Eastern'],
  },
  {
    value: 'golden-hour-drama' as const,
    label: 'Golden Hour Drama',
    color: '#6366f1',
    prompt: 'Cinematic golden hour drama style, photorealistic rendering bathed in warm amber and honey-gold natural light, rich saturated color palette with warm skin tones and sun-kissed highlights, soft diffused backlighting with long golden shadows and lens flare, shallow depth of field with creamy bokeh backgrounds, fine photographic skin detail and natural textures, intimate and emotionally warm atmosphere inspired by prestige television dramas and romantic cinema.',
    tags: ['Western'],
  },
  {
    value: 'gritty-urban-realism' as const,
    label: 'Gritty Urban Realism',
    color: '#6366f1',
    prompt: 'Cinematic gritty urban realism style, photorealistic rendering with raw documentary-like grain and handheld camera texture, muted desaturated color palette with concrete grays, faded signage tones, and washed-out streetlight yellows, harsh overhead and mixed artificial lighting with deep under-eye shadows, sharp unflattering detail on pores and weathered surfaces, cluttered and lived-in environments, tense and unvarnished atmosphere inspired by street photography and social realism cinema.',
    tags: ['Western', 'Pop Culture'],
  },
  {
    value: 'noir-thriller' as const,
    label: 'Noir Thriller',
    color: '#374151',
    prompt: 'Cinematic noir thriller style, photorealistic rendering with deep contrast and harsh chiaroscuro lighting, desaturated color palette with cold steel blues and muted teals punctuated by isolated warm highlights, dramatic low-key lighting with strong directional shadows and silhouette play, sharp photographic detail on skin textures and fabric, rain-slicked surfaces with specular reflections, tense and suspenseful atmosphere inspired by modern neo-noir cinema and detective thriller films.',
    tags: ['Western', 'Pop Culture'],
  }
,
  {
    value: 'chibi-animal-cartoon' as const,
    label: 'Chibi Animal Cartoon',
    color: '#fda4af',
    prompt: 'Cute chibi animal cartoon style, super-simplified round white character bodies with bold black outlines, minimal internal detail, flat coloring with no shading on characters, simple dot or circle eyes with simple curved mouths, oversized heads with small stubby bodies, bright saturated accent colors for accessories and clothing, clean digital animation look, semi-realistic painted backgrounds contrasting with the simple flat character designs, warm bright color palette, playful whimsical atmosphere, Chinese donghua-inspired character design',
    tags: ['Cute Chibi', 'Eastern'],
  }
] as const

export type VisualStyle = (typeof VISUAL_STYLES)[number]['value']

export const DEFAULT_STYLE: VisualStyle = '2d-comic'

/**
 * Tag values that can be assigned to styles. These are the actual enum values
 * stored on style objects in `style.json` and returned by the API.
 */
export const STYLE_TAGS = [
  'Pop Culture',
  'Anime',
  'Illustrative',
  'Korean',
  'Western',
  '3D',
  'Eastern',
  'Cute Chibi',
] as const

/**
 * Filter tabs shown in the Style Library modal.
 * "All", "Recently Used", and "My Styles" are virtual filters (not stored on styles).
 */
export const STYLE_FILTER_TABS = [
  'All',
  'Recently Used',
  'My Styles',
  ...STYLE_TAGS,
] as const

/**
 * Tabs that are disabled (not yet implemented).
 */
export const DISABLED_FILTER_TABS = new Set(['Recently Used', 'My Styles'])

/**
 * Get style option by value
 */
export function getStyleOption(value: string) {
  return VISUAL_STYLES.find((s) => s.value === value)
}
